import { useMutation, useQuery } from '@apollo/client';
import { Footer, NavBar } from '@gasbuddy/react-consumer-components';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { version } from '../../../../package.json';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useConfig from '../../../lib/hooks/useConfig';
import useGeoIpInfo from '../../../lib/hooks/useGeoIpInfo';
import usePageContext from '../../../lib/hooks/usePageContext';
import useProfile from '../../../lib/hooks/useProfile';
import { truncateForDisplay } from '../../../lib/utils';
import canShowPrivacyOptions from '../../../lib/utils/canShowPrivacyOptions';
import { ANALYTICS_EVENTS, COUNTRIES_WITH_PAY } from '../../constants';

const GET_USER_PREFERENCES = gql`
  query GetUserPreferences {
    userPreferences {
      language
    }
  }
`;

const UPDATE_USER_PREFERENCES = gql`
  mutation UpdateUserPreferences($preferences: UserPreferencesInput!) {
    updateUserPreferences(preferences: $preferences) {
      language
    }
  }
`;

export default function PageLayout({ children }) {
  const { t, i18n } = useTranslation(['recalls', 'feature']);
  const analytics = useAnalytics();
  const history = useHistory();
  const { country } = useGeoIpInfo();
  const { pageName } = usePageContext();
  const { payHost, trackerHost } = useConfig();
  const { isLoggedIn, memberName: username, profileImageUrl: avatar } = useProfile();
  const isLoggedOut = !isLoggedIn;

  const { data: userPreferencesData, loading: preferencesLoading } = useQuery(GET_USER_PREFERENCES, {
    skip: !isLoggedIn,
  });

  const [updateUserPreferences] = useMutation(UPDATE_USER_PREFERENCES, {
    onCompleted: () => {
      // Successfully updated locale preference in database
    },
    onError: () => {
      // Handle locale update error silently
    },
  });

  // Set initial locale based on user preferences
  useEffect(() => {
    if (isLoggedIn && !preferencesLoading && userPreferencesData?.userPreferences?.language) {
      const userLanguagePreference = userPreferencesData.userPreferences.language;
      if (userLanguagePreference !== i18n.language) {
        i18n.changeLanguage(userLanguagePreference);
        // The language change will automatically trigger the cookie/localStorage sync via i18n event listener
      }
    } else if (!isLoggedIn) {
      // For logged-out users, ensure we preserve their language selection from cookies
      // This is especially important after signup/logout when they return from identity-web
      const currentLanguage = i18n.language;
      // Double-check that the current language matches what's stored (should already be handled by languagePreferenceManager)
      // This acts as a safety net for any edge cases
      if (currentLanguage && currentLanguage !== 'en') {
        // Ensure the language is also stored in localStorage for client-side persistence
        try {
          localStorage.setItem('gb_preferred_locale', currentLanguage);
        } catch {
          // Ignore localStorage errors
        }
      }
    }
  }, [isLoggedIn, preferencesLoading, userPreferencesData, i18n]);

  const handleNavbarItemClick = useCallback((e) => {
    const { currentTarget, target } = e;
    const linkName = target.innerText;

    // menu item is in a span. Current target gives us the element that actually has the event
    const linkTarget = currentTarget.target;
    const href = currentTarget.attributes.href.value;

    if (linkName === t('vehicleRecalls', { ns: 'recalls' })) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.OPEN_RECALL_SEARCH_PAGE,
        attributes: {
          Page: linkName,
          source: 'navbar',
        },
      });
    } else {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.MAIN_MENU_ITEM_CLICK,
        attributes: {
          name: linkName,
          href,
          'Page Type': pageName,
        },
      });
    }

    const isRelative = href.charAt(0) === '/' && href.charAt(1) !== '/';
    const opensNewTab = linkTarget === '_blank';
    const shouldRedirect = [
      '/login',
      '/logout',
      '/signup',
      '/password/change',
    ].includes(href) || (isLoggedOut && href.startsWith('/account/'));

    if (isRelative && !opensNewTab && !shouldRedirect) {
      e.preventDefault();
      history.push(href);
    }
  }, [t, isLoggedOut, analytics, pageName, history]);

  const generateAccountObject = useMemo(() => {
    if (!username) {
      return undefined;
    }

    return {
      username: truncateForDisplay(username),
      avatar,
    };
  }, [avatar, username]);

  const handleFooterItemClick = useCallback((e) => {
    const { currentTarget, target } = e;
    const linkName = target.innerText;
    const href = currentTarget.attributes.href.value;

    analytics.tagEvent({
      name: ANALYTICS_EVENTS.FOOTER_ITEM_CLICK,
      attributes: {
        name: linkName,
        href,
        'Page Type': pageName,
      },
    });
  }, [analytics, pageName]);

  const handleLocaleChange = useCallback((newLocale) => {
    // Update the language in i18n (this will automatically trigger cookie/localStorage sync)
    i18n.changeLanguage(newLocale);

    // Track the locale change event
    analytics.tagEvent({
      name: ANALYTICS_EVENTS.LOCALE_CHANGE,
      attributes: {
        newLocale,
        'Page Type': pageName,
      },
    });

    // If user is logged in, update their locale preference in the database
    if (isLoggedIn) {
      updateUserPreferences({
        variables: {
          preferences: {
            language: newLocale,
          },
        },
      });
    }
  }, [i18n, analytics, pageName, isLoggedIn, updateUserPreferences]);

  const applyCcpa = canShowPrivacyOptions(country);

  const mainMenuItems = [...NavBar.defaultProps.mainLinkGroups];
  let footerSitemap = [...Footer.defaultProps.sitemap];

  const showPayLink = country && COUNTRIES_WITH_PAY.includes(country.toUpperCase());

  if (!showPayLink) {
    mainMenuItems.splice(2, 1);
    const footerFeatures = footerSitemap.find(group => group.key === 'features');
    if (footerFeatures) {
      footerFeatures.siteLinks = footerFeatures.siteLinks.filter(link => !link.href.match(/.*(pay|credit|gasback)/));
    }
    footerSitemap = [
      footerFeatures,
      ...footerSitemap.filter(group => group.key !== 'features'),
    ];
  }

  return (
    <Fragment>
      <NavBar
        account={generateAccountObject}
        consumerHost="" // Make consumer-web URLs relative
        identityHost="" // Make identity-web URLs relative
        localeButtonProps={{
          currentLocale: i18n.language,
          isVisible: true,
          onSubmit: handleLocaleChange,
        }}
        mainLinkGroups={mainMenuItems}
        onMenuItemClick={handleNavbarItemClick}
        payHost={payHost} // Make payment-web URLs environment-friendly
        rootContainerId="root"
        trackerHost={trackerHost} // Make tracker-web URLs environment-friendly
      />
      {children}
      <Footer
        applyCcpa={applyCcpa}
        consumerHost="" // Make consumer-web URLs relative
        identityHost="" // Make identity-web URLs relative
        onItemClick={handleFooterItemClick}
        payHost={payHost} // Make payment-web URLs environment-friendly
        sitemap={footerSitemap}
        trackerHost={trackerHost} // Make tracker-web URLs environment-friendly
        version={version}
      />
    </Fragment>
  );
}

PageLayout.propTypes = {
  children: PropTypes.node,
};

PageLayout.defaultProps = {
  children: undefined,
};
